import { ProductTypes } from "@/data/enums/products";
import { RegexMatch, AdminRoutes } from "@/data/enums/router";
import type { Route } from "vue-router";
import CProdsListing from "@/components/app/global/contractProducts/cProdsListing.vue";
import {
  DateCancelledSorter,
  DatePurchasedSorter,
  DefaultCProdSorters,
  StatusSorter
} from "@/data/sorters/contractProducts";
import {
  statusParam,
  CatalogueProductIdParam
} from "@/data/filters/contractProducts";
import { CatalogueProductCProdFilters } from "@/data/filters/contractProducts";
import { ContractStatusCodes } from "@upmind-automation/types";

export default [
  {
    path: "catalogue",
    name: "adminProductCatalogue",
    components: {
      default: () => import("@/views/admin/catalogue/index.vue")
    },
    meta: {
      requiresBrand: true,
      title: "_.catalogue",
      backTo: "_.catalogue"
    },
    props: {
      default: {
        routes: require("./menu").catalogueTabigation
      }
    },
    redirect: { name: "adminCatalogueProducts" },
    children: [
      {
        path: "products",
        name: "adminCatalogueProducts",
        component: () => import("@/views/admin/catalogue/products/index.vue"),
        props: {
          productTypes: [
            ProductTypes.SINGLE_PRODUCT,
            ProductTypes.PRODUCT_BUNDLE
          ],
          routes: require("./menu").productsQuickFilters,
          id: "PR1"
        },
        meta: {
          title: "_.products",
          backTo: "_.products",
          auth: ["staff_list_products", "list_product_categories"]
        }
      },
      {
        path: "options",
        name: "adminCatalogueOptions",
        component: () => import("@/views/admin/catalogue/products/index.vue"),
        props: {
          productTypes: [ProductTypes.PRODUCT_OPTION],
          routes: require("./menu").productOptionsQuickFilters,
          id: "PRO1"
        },
        meta: {
          title: "_.product_options",
          backTo: "_.product_options",
          auth: ["staff_list_products", "list_product_options_categories"]
        }
      },
      {
        path: "attributes",
        name: "adminCatalogueAttributes",
        component: () => import("@/views/admin/catalogue/products/index.vue"),
        props: {
          productTypes: [ProductTypes.PRODUCT_ATTRIBUTE],
          routes: require("./menu").productAttributesQuickFilters,
          id: "PRA1"
        },
        meta: {
          title: "_.product_attributes",
          backTo: "_.product_attributes",
          auth: ["staff_list_products", "list_product_attributes_categories"]
        }
      }
    ]
  },
  {
    path: `catalogue/product/:productId(${RegexMatch.UUID})`,
    name: "adminCatalogueProduct",
    component: () => import("@/views/admin/catalogue/product/index.vue"),
    props: {
      productTypes: [ProductTypes.SINGLE_PRODUCT, ProductTypes.PRODUCT_BUNDLE],
      routes: require("./menu").productOverview
    },
    redirect: { name: "adminCatalogueProductDetails" },
    meta: {
      auth: ["staff_get_product", "list_product_categories"],
      backTo: "_.catalogue",
      requiresBrand: true,
      title: "_.catalogue"
    },
    children: [
      {
        path: "details",
        name: "adminCatalogueProductDetails",
        component: () =>
          import("@/views/admin/catalogue/product/details/index.vue"),
        meta: {
          title: "_.product_details",
          backTo: "_.product_details"
        }
      },
      {
        path: "billing",
        name: "adminCatalogueProductBilling",
        component: () =>
          import("@/views/admin/catalogue/product/billing/index.vue"),
        meta: {
          title: "_.product_billing",
          backTo: "_.product_billing"
        }
      },
      {
        path: "automation",
        name: "adminCatalogueProductAutomation",
        component: () =>
          import("@/views/admin/catalogue/product/automation/index.vue"),
        meta: {
          title: "_.product_automation",
          backTo: "_.product_automation"
        }
      },
      {
        path: "options",
        name: "adminCatalogueProductOptions",
        component: () =>
          import("@/views/admin/catalogue/product/options/index.vue"),
        props: true,
        meta: {
          title: "_.product_options",
          backTo: "_.product_options"
        }
      },
      {
        path: "attributes",
        name: "adminCatalogueProductAttributes",
        component: () =>
          import("@/views/admin/catalogue/product/attributes/index.vue"),
        props: true,
        meta: {
          title: "_.product_attributes",
          backTo: "_.product_attributes"
        }
      },
      {
        path: "recommendations",
        name: "adminCatalogueProductRecommendations",
        component: () =>
          import("@/views/admin/catalogue/product/relatedProducts/index.vue"),
        meta: {
          title: "_.product_recommendations",
          backTo: "_.product_recommendations"
        }
      },
      {
        path: "provisioning",
        name: "adminCatalogueProductProvisioning",
        component: () =>
          import("@/views/admin/catalogue/product/provisioning/index.vue"),
        meta: {
          title: "_.product_provisioning",
          backTo: "_.product_provisioning"
        }
      },
      {
        path: "notifications",
        name: "adminCatalogueProductNotifications",
        component: () =>
          import("@/views/admin/catalogue/product/triggers/index.vue"),
        meta: {
          title: "_.product_notifications",
          backTo: "_.product_notifications"
        }
      },
      {
        path: "templates",
        component: () =>
          import("@/views/admin/catalogue/product/templates/index.vue"),
        props: {
          routes: require("./menu").CatalogueProductTemplatesTabigation
        },
        meta: {
          title: "_.client_area_templates",
          backTo: "_.client_area_templates"
        },
        children: [
          {
            path: "",
            name: "admin__catalogueProductTemplates",
            redirect: "slots"
          },
          {
            path: "slots",
            name: "admin__catalogueProductTemplateSlots",
            component: () =>
              import(
                "@/views/admin/catalogue/product/templates/slots/index.vue"
              ),
            auth: ["list_client_area_template_slots"]
          },
          {
            path: "templates",
            name: "admin__catalogueProductTemplateTemplates",
            component: () =>
              import(
                "@/views/admin/catalogue/product/templates/templates/index.vue"
              ),
            auth: ["list_templates"]
          }
        ]
      },
      {
        path: "upgrade-downgrade",
        name: "admin__catalogueProductUpgradeDowngrade",
        component: () =>
          import(
            "@/views/admin/settings/product-catalogue/product/migrations/index.vue"
          )
      },
      {
        path: "products",
        name: "adminCatalogueProductContractProducts",
        component: () =>
          import("@/views/admin/catalogue/product/contractProducts/index.vue"),
        props: () => ({
          routes: require("./menu").catalogueProductsCPsTabigation
        }),
        redirect: { name: AdminRoutes.CATALOGUE_PRODUCT_ALL_CPS },
        meta: {
          title: "_.products_and_services",
          backTo: "_.products_and_services"
        },
        children: [
          {
            path: "all",
            name: AdminRoutes.CATALOGUE_PRODUCT_ALL_CPS,
            component: CProdsListing,
            props: (route: Route) => ({
              showQuickSearch: false,
              availableSorters: DefaultCProdSorters,
              availableFilters: () => CatalogueProductCProdFilters(),
              routeFilters: () => ({
                ...CatalogueProductIdParam(route.params.productId)
              })
            }),
            meta: {
              backTo: "_.products_and_services",
              title: "_.products_and_services"
            }
          },
          {
            path: "active",
            name: AdminRoutes.CATALOGUE_PRODUCT_ACTIVE_CPS,
            component: CProdsListing,
            props: (route: Route) => ({
              showQuickSearch: false,
              availableSorters: DefaultCProdSorters,
              availableFilters: () => CatalogueProductCProdFilters(),
              routeFilters: () => ({
                ...CatalogueProductIdParam(route.params.productId),
                ...statusParam([
                  ContractStatusCodes.ACTIVE,
                  ContractStatusCodes.AWAITING_ACTIVATION,
                  ContractStatusCodes.PENDING,
                  ContractStatusCodes.SUSPENDED
                ])
              })
            }),
            meta: {
              backTo: "_.active_products_and_services",
              title: "_.active_products_and_services"
            }
          },
          {
            path: "cancelled",
            name: AdminRoutes.CATALOGUE_PRODUCT_CANCELLED_CPS,
            component: CProdsListing,
            props: (route: Route) => ({
              showQuickSearch: false,
              availableSorters: () => [
                StatusSorter(),
                DateCancelledSorter(),
                DatePurchasedSorter()
              ],
              availableFilters: () => CatalogueProductCProdFilters(),
              routeFilters: () => ({
                ...CatalogueProductIdParam(route.params.productId),
                ...statusParam([
                  ContractStatusCodes.CANCELLED,
                  ContractStatusCodes.CLOSED
                ])
              })
            }),
            meta: {
              backTo: "_.cancelled_products_and_services",
              title: "_.cancelled_products_and_services"
            }
          }
        ]
      }
    ]
  },
  {
    path: `catalogue/category/:categoryId(${RegexMatch.UUID})`,
    name: "adminCatalogueCategory",
    component: () => import("@/views/admin/catalogue/category/index.vue"),
    props: (route: Route) => ({
      categoryId: route.params.categoryId,
      productTypes: [ProductTypes.SINGLE_PRODUCT, ProductTypes.PRODUCT_BUNDLE],
      routes: require("./menu").categoryOverview
    }),
    redirect: { name: "adminCatalogueCategoryDetails" },
    meta: {
      auth: ["get_product_category", "list_product_categories"],
      backTo: "_.catalogue",
      requiresBrand: true,
      title: "_.catalogue"
    },
    children: [
      {
        path: "details",
        name: "adminCatalogueCategoryDetails",
        component: () =>
          import("@/views/admin/catalogue/category/details/index.vue"),
        meta: {
          title: "_.category_details",
          backTo: "_.category_details"
        }
      },
      {
        path: "products",
        name: "adminCatalogueCategoryProducts",
        component: () =>
          import("@/views/admin/catalogue/category/products/index.vue"),
        meta: {
          title: "_.category_products",
          backTo: "_.category_products"
        }
      },
      {
        path: "provisioning",
        name: "adminCatalogueCategoryProvisioning",
        component: () =>
          import("@/views/admin/catalogue/category/provisioning/index.vue"),
        meta: {
          title: "_.category_provisioning",
          backTo: "_.category_provisioning"
        }
      }
    ]
  },
  {
    path: `catalogue/option/:productId(${RegexMatch.UUID})`,
    name: "adminCatalogueOption",
    component: () => import("@/views/admin/catalogue/product/index.vue"),
    props: {
      productTypes: [ProductTypes.PRODUCT_OPTION],
      routes: require("./menu").productOptionOverview
    },
    redirect: { name: "adminCatalogueOptionDetails" },
    meta: {
      auth: ["staff_get_product", "list_product_options_categories"],
      backTo: "_.catalogue",
      requiresBrand: true,
      title: "_.catalogue"
    },
    children: [
      {
        path: "details",
        name: "adminCatalogueOptionDetails",
        component: () =>
          import("@/views/admin/catalogue/product/details/index.vue"),
        meta: {
          title: "_.product_option_details",
          backTo: "_.product_option_details"
        }
      },
      {
        path: "billing",
        name: "adminCatalogueOptionBilling",
        component: () =>
          import("@/views/admin/catalogue/product/billing/index.vue"),
        meta: {
          title: "_.product_option_billing",
          backTo: "_.product_option_billing"
        }
      }
    ]
  },
  {
    path: `catalogue/option-category/:categoryId(${RegexMatch.UUID})`,
    name: "adminCatalogueOptionCategory",
    component: () => import("@/views/admin/catalogue/category/index.vue"),
    props: (route: Route) => ({
      categoryId: route.params.categoryId,
      productTypes: [ProductTypes.PRODUCT_OPTION],
      routes: require("./menu").optionCategoryOverview
    }),
    redirect: { name: "adminCatalogueOptionCategoryDetails" },
    meta: {
      auth: ["get_product_options_category", "list_product_options_categories"],
      backTo: "_.catalogue",
      requiresBrand: true,
      title: "_.catalogue"
    },
    children: [
      {
        path: "details",
        name: "adminCatalogueOptionCategoryDetails",
        component: () =>
          import("@/views/admin/catalogue/category/details/index.vue"),
        meta: {
          title: "_.category_details",
          backTo: "_.category_details"
        }
      },
      {
        path: "products",
        name: "adminCatalogueOptionCategoryProducts",
        component: () =>
          import("@/views/admin/catalogue/category/products/index.vue"),
        meta: {
          title: "_.category_options",
          backTo: "_.category_options"
        }
      }
    ]
  },
  {
    path: `catalogue/attribute/:productId(${RegexMatch.UUID})`,
    name: "adminCatalogueAttribute",
    component: () => import("@/views/admin/catalogue/product/index.vue"),
    props: {
      productTypes: [ProductTypes.PRODUCT_ATTRIBUTE],
      routes: require("./menu").productAttributeOverview
    },
    meta: {
      auth: ["staff_get_product", "list_product_attributes_categories"],
      backTo: "_.catalogue",
      requiresBrand: true,
      title: "_.catalogue"
    },
    redirect: { name: "adminCatalogueAttributeDetails" },
    children: [
      {
        path: "details",
        name: "adminCatalogueAttributeDetails",
        component: () =>
          import("@/views/admin/catalogue/product/details/index.vue"),
        meta: {
          title: "_.product_attribute_details",
          backTo: "_.product_attribute_details"
        }
      }
    ]
  },
  {
    path: `catalogue/attribute-category/:categoryId(${RegexMatch.UUID})`,
    name: "adminCatalogueAttributeCategory",
    component: () => import("@/views/admin/catalogue/category/index.vue"),
    props: (route: Route) => ({
      categoryId: route.params.categoryId,
      productTypes: [ProductTypes.PRODUCT_ATTRIBUTE],
      routes: require("./menu").attributeCategoryOverview
    }),
    redirect: { name: "adminCatalogueAttributeCategoryDetails" },
    meta: {
      auth: [
        "get_product_attributes_category",
        "list_product_attributes_categories"
      ],
      backTo: "_.catalogue",
      requiresBrand: true,
      title: "_.catalogue"
    },
    children: [
      {
        path: "details",
        name: "adminCatalogueAttributeCategoryDetails",
        component: () =>
          import("@/views/admin/catalogue/category/details/index.vue"),
        meta: {
          title: "_.category_details",
          backTo: "_.category_details"
        }
      },
      {
        path: "products",
        name: "adminCatalogueAttributeCategoryProducts",
        component: () =>
          import("@/views/admin/catalogue/category/products/index.vue"),
        meta: {
          title: "_.category_attributes",
          backTo: "_.category_attributes"
        }
      }
    ]
  }
];
